class ProgressBar extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.progressBar = this.querySelector('[data-progress-bar]');
    if (!this.progressBar) return;

    this.milestones = this.progressBar.querySelectorAll('[data-tier]');
    this.milestoneLabels = this.progressBar.querySelectorAll('[data-reward]');
    this.progressFill = this.progressBar.querySelector('[data-progress]');
    this.achievementEl = this.querySelector('[data-progress-achievement]');

    // Watch free shipping notice for changes (primary method)
    const freeShippingNotice = document.getElementById('free-shipping-notice');
    if (freeShippingNotice) {
      const getCartTotalFromFreeShipping = () => {
        const container = freeShippingNotice.querySelector('[data-minimum]');
        if (!container) return null;
        
        const minimum = parseInt(container.dataset.minimum) || 0;
        if (minimum === 0) return null;
        
        const progressBar = freeShippingNotice.querySelector('.free-shipping-notice__bar');
        if (!progressBar) return null;
        
        const progressStyle = progressBar.style.getPropertyValue('--progress');
        if (!progressStyle) return null;
        
        const progressPercent = parseFloat(progressStyle.replace('%', '')) || 0;
        return Math.round((progressPercent / 100) * minimum);
      };
      
      this.freeShippingObserver = new MutationObserver(() => {
        const cartTotal = getCartTotalFromFreeShipping();
        if (cartTotal !== null) {
          this.updateProgress({ total_price: cartTotal });
        } else {
          this.fetchCartData();
        }
      });
      this.freeShippingObserver.observe(freeShippingNotice, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'data-minimum'],
        characterData: true
      });
    }
    
    // Fallback: Poll cart API periodically
    let lastApiCartTotal = null;
    const pollCartAPI = async () => {
      try {
        const response = await fetch('/cart.js');
        if (response.ok) {
          const cart = await response.json();
          const currentTotal = parseInt(cart.total_price) || 0;
          if (currentTotal !== lastApiCartTotal) {
            lastApiCartTotal = currentTotal;
            this.updateProgress(cart);
          }
        }
      } catch (error) {
        // Silently fail
      }
    };
    setInterval(pollCartAPI.bind(this), 2000);

    // Initial update - always update to show minimum 5% fill
    const initialCartTotal = parseInt(this.progressBar.dataset.cartTotal) || 0;
    this.updateProgress({ total_price: initialCartTotal });
  }

  updateProgress(cart) {
    if (!cart) {
      this.fetchCartData();
      return;
    }

    const cartTotal = parseInt(cart.total_price) || 0;
    
    // Re-query elements in case DOM changed
    if (!this.milestones || this.milestones.length === 0) {
      this.milestones = this.progressBar.querySelectorAll('[data-tier]');
    }
    if (!this.milestoneLabels || this.milestoneLabels.length === 0) {
      this.milestoneLabels = this.progressBar.querySelectorAll('[data-reward]');
    }
    if (!this.progressFill) {
      this.progressFill = this.progressBar.querySelector('[data-progress]');
    }
    
    // Calculate progress based on milestones
    // Positions: evenly distributed in middle 80% (10% to 90%) with gaps at start/end
    const milestonesArray = Array.from(this.milestones);
    const milestoneCount = milestonesArray.length;
    const milestonesWithPositions = milestonesArray.map((milestone, index) => {
      const amount = parseInt(milestone.dataset.amount) || 0;
      // Read position from DOM (set by Liquid template)
      // Check for right: 0 (last tier) or left: X% (other tiers)
      const leftStyle = milestone.style.left;
      const rightStyle = milestone.style.right;
      let position = 0;
      
      if (rightStyle === '0px' || rightStyle === '0') {
        // Last tier positioned with right: 0
        position = 100;
      } else if (leftStyle) {
        const match = leftStyle.match(/(\d+\.?\d*)%/);
        if (match) {
          position = parseFloat(match[1]);
        }
      }
      
      // Fallback calculation if position not found - matches Liquid template logic
      // Last tier is always at 100%
      const isLastTier = index === milestoneCount - 1;
      const isFirstTier = index === 0;
      if (position === 0 && milestoneCount > 0) {
        if (isLastTier) {
          position = 100;
        } else if (milestoneCount === 1) {
          position = 100;
        } else if (milestoneCount === 2) {
          position = 50;
        } else if (milestoneCount === 3) {
          position = index === 0 ? 20 : index === 1 ? 58 : 100;
        } else {
          const divisor = milestoneCount - 1;
          position = (index / divisor) * 100;
        }
      }
      
      return { amount, position, element: milestone };
    });
    
    const sortedByAmount = [...milestonesWithPositions].sort((a, b) => a.amount - b.amount);
    let progress = 5; // Default minimum 5% fill
    
    if (sortedByAmount.length === 0) {
      progress = 5; // No milestones, show minimum fill
    } else if (cartTotal <= 0) {
      progress = 5; // Maintain 5% minimum fill
    } else if (cartTotal >= sortedByAmount[sortedByAmount.length - 1].amount) {
      // Cart reaches or exceeds the last milestone - last milestone is always at 100%
      progress = 100;
    } else {
      // Cart is below the last milestone - find which two milestones it's between
      let prevMilestone = null;
      let nextMilestone = null;
      
      for (let i = 0; i < sortedByAmount.length; i++) {
        if (cartTotal < sortedByAmount[i].amount) {
          nextMilestone = sortedByAmount[i];
          // If before first milestone, prevMilestone is at position 0 (start of bar)
          prevMilestone = i > 0 ? sortedByAmount[i - 1] : { amount: 0, position: 0 };
          break;
        }
      }
      
      if (prevMilestone && nextMilestone) {
        // Interpolate between two milestones
        const prevVisual = milestonesWithPositions.find(m => m.amount === prevMilestone.amount) || prevMilestone;
        const nextVisual = milestonesWithPositions.find(m => m.amount === nextMilestone.amount) || nextMilestone;
        
        const amountRange = nextMilestone.amount - prevMilestone.amount;
        const positionRange = nextVisual.position - prevVisual.position;
        
        if (amountRange > 0) {
          const progressInRange = (cartTotal - prevMilestone.amount) / amountRange;
          progress = prevVisual.position + (progressInRange * positionRange);
        } else {
          progress = prevVisual.position;
        }
      } else {
        // Cart is at or above all milestones (shouldn't happen due to earlier check, but handle it)
        const lastMilestone = sortedByAmount[sortedByAmount.length - 1];
        const lastVisual = milestonesWithPositions.find(m => m.amount === lastMilestone.amount) || lastMilestone;
        progress = lastVisual.position;
      }
    }
    
    // Ensure minimum 5% and maximum 100%
    progress = Math.max(5, Math.min(progress, 100));

    // Update progress bar fill
    if (this.progressFill) {
      this.progressFill.style.setProperty('--progress', `${progress}%`);
      this.progressFill.style.width = `${progress}%`;
      this.progressFill.dataset.progress = progress;
    }

    // Update milestones
    const reachedIconUrl = this.progressBar.dataset.reachedIcon || '';
    
    this.milestones.forEach((milestone) => {
      const milestoneAmount = parseInt(milestone.dataset.amount) || 0;
      const isReached = cartTotal >= milestoneAmount;
      
      milestone.classList.toggle('milestone--reached', isReached);
      
      const iconContainer = milestone.querySelector('.milestone-icon-container') || milestone.querySelector('div[class*="tw-w-"]');
      if (iconContainer) {
        // Update background color using inline styles to override Liquid template inline styles
        if (isReached) {
          iconContainer.style.setProperty('background-color', 'rgb(var(--btn-bg-color))', 'important');
          iconContainer.style.setProperty('border-color', 'rgb(var(--btn-bg-color))', 'important');
        } else {
          iconContainer.style.setProperty('background-color', '#F4EFE9', 'important');
          iconContainer.style.setProperty('border-color', 'transparent', 'important');
        }
        
        // Handle icon/image swapping
        const existingImg = iconContainer.querySelector('img');
        const existingSvg = iconContainer.querySelector('svg');
        
        if (isReached) {
          // Show reached icon/tick
          if (reachedIconUrl && existingImg) {
            existingImg.src = reachedIconUrl;
            existingImg.style.opacity = '1';
            existingImg.style.display = '';
          } else if (existingSvg) {
            existingSvg.classList.add('checkmark-icon');
            existingSvg.classList.remove('tw-text-transparent', 'tw-opacity-0');
            existingSvg.classList.add('tw-text-[rgb(var(--btn-text-color,255,255,255))]', 'tw-opacity-100');
          }
        } else {
          // Show tier icon (unreached state)
          const tierIconUrl = milestone.dataset.tierIcon;
          if (tierIconUrl && existingImg) {
            existingImg.src = tierIconUrl;
            existingImg.style.opacity = '1';
            existingImg.style.display = '';
          } else if (existingSvg && !existingImg) {
            existingSvg.classList.remove('checkmark-icon', 'tw-text-[rgb(var(--btn-text-color,255,255,255))]', 'tw-opacity-100');
            existingSvg.classList.add('tw-text-transparent', 'tw-opacity-0');
          }
        }
      }
    });

    // Update milestone labels and collect reached rewards
    const reachedRewards = [];
    
    this.milestoneLabels.forEach((label) => {
      const milestoneAmount = parseInt(label.dataset.amount) || 0;
      const isReached = cartTotal >= milestoneAmount;
      
      label.classList.toggle('milestone-label--reached', isReached);
      
      const rewardText = label.querySelector('div');
      if (rewardText) {
        if (isReached) {
          rewardText.classList.remove('tw-text-[#685532]');
          rewardText.classList.add('tw-text-[#685532]', 'tw-font-medium');
        } else {
          rewardText.classList.remove('tw-text-[#685532]', 'tw-font-medium');
          rewardText.classList.add('tw-text-[#685532]');
        }
      }
      
      if (isReached) {
        const reward = label.dataset.reward || '';
        if (reward) {
          reachedRewards.push(reward);
        }
      }
    });

    // Update achievement text
    this.updateAchievementText(reachedRewards, cartTotal);

    // Update data attribute
    this.progressBar.dataset.cartTotal = cartTotal;
  }

  async fetchCartData() {
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cart = await response.json();
        if (cart?.total_price !== undefined) {
          this.updateProgress(cart);
          return;
        }
      }
    } catch (error) {
      // Silently fail
    }
    
    // Fallback to initial cart total
    const initialCartTotal = parseInt(this.progressBar?.dataset.cartTotal) || 0;
    this.updateProgress({ total_price: initialCartTotal });
  }

  updateAchievementText(reachedRewards, cartTotal) {
    if (!this.achievementEl) return;
    
    const achievementTextEl = this.achievementEl.querySelector('[data-achievement-template]');
    if (!achievementTextEl) return;

    const allReachedTemplate = achievementTextEl.dataset.allReachedTemplate || 
      'Congratulations! You\'ve unlocked all rewards!';
    const totalMilestones = parseInt(achievementTextEl.dataset.totalMilestones) || 0;

    // Find the next unreached tier
    let nextTierAmount = 0;
    let nextTierReward = '';
    
    if (this.milestoneLabels && this.milestoneLabels.length > 0) {
      const milestonesArray = Array.from(this.milestoneLabels).map(label => ({
        amount: parseInt(label.dataset.amount) || 0,
        reward: label.dataset.reward || ''
      }));
      
      // Sort by amount
      milestonesArray.sort((a, b) => a.amount - b.amount);
      
      // Find first unreached milestone
      for (const milestone of milestonesArray) {
        if (cartTotal < milestone.amount) {
          nextTierAmount = milestone.amount;
          nextTierReward = milestone.reward;
          break;
        }
      }
    }

    // Determine which text to show
    if (totalMilestones > 0 && reachedRewards.length === totalMilestones && allReachedTemplate) {
      achievementTextEl.textContent = allReachedTemplate;
    } else if (nextTierAmount > 0 && nextTierReward) {
      const amountNeeded = nextTierAmount - cartTotal;
      if (amountNeeded > 0) {
        // Format money - Shopify cart.js returns prices in cents
        const formattedAmount = this.formatMoney(amountNeeded);
        achievementTextEl.textContent = `Spend ${formattedAmount} more for ${nextTierReward}`;
      } else {
        achievementTextEl.textContent = allReachedTemplate || `You've unlocked ${nextTierReward}!`;
      }
    } else {
      const defaultTemplate = achievementTextEl.dataset.defaultTemplate || 
        'Add items to your cart to unlock rewards!';
      achievementTextEl.textContent = defaultTemplate;
    }
  }

  formatMoney(cents) {
    // Get currency symbol from progress bar data attribute
    const currencySymbol = this.progressBar?.dataset.currencySymbol || 
      document.querySelector('[data-currency]')?.dataset.currency || 
      '$';
    
    // Convert cents to currency amount
    const amount = (cents / 100);
    
    // Check if it's a whole number by checking if cents is divisible by 100
    // This avoids floating point precision issues
    if (cents % 100 === 0) {
      // Whole number - no decimals
      return `${currencySymbol}${Math.round(amount)}`;
    } else {
      // Has decimals - format with 2 decimal places and remove trailing zeros
      const formatted = amount.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
      return `${currencySymbol}${formatted}`;
    }
  }
}

customElements.define('progress-bar-block', ProgressBar);

// Initialize existing progress bars on page load
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-progress-bar]').forEach((bar) => {
    if (!bar.closest('progress-bar-block')) {
      const wrapper = document.createElement('progress-bar-block');
      bar.parentNode.insertBefore(wrapper, bar);
      wrapper.appendChild(bar);
    }
  });
});
